import { CheckCircle2, Loader2 } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import { Skeleton } from '@/components/ui/skeleton';
import { useIsMobile } from '@/hooks/use-mobile';
import { useI18nContext } from '@/i18n/i18n-react';
import { mobileLayer } from '@/lib/mobile-layer';
import type { LinkFlowState } from '@/lib/test-device-link';
import { cn } from '@/lib/utils';

export interface TestDeviceSheetProps {
  open: boolean;
  onOpenChange(open: boolean): void;
  phase: LinkFlowState['phase'];
  /** Deep link with the one-time ticket; `null` while the ticket is requested. */
  deepLink: string | null;
  /** Requesting the ticket failed. */
  ticketError: boolean;
  /** Requests a new ticket and restarts the 2 min wait. */
  onRetry(): void;
  onSend(): void;
  isSending: boolean;
  appStoreUrl: string | null;
  playStoreUrl: string | null;
  /** Called right before the deep link opens (e.g. to bypass a leave guard, design R11). */
  onOpenApp?(): void;
}

/**
 * Guided link of the admin's test phone (PUSH-08 AC 3–5, PUSH-09 AC 2): a
 * sheet with "Abrir o app da loja" below 768px, a dialog with the QR code
 * from 768px up; both show the link status. The deep link (with the ticket)
 * only shows while waiting, so an expired ticket never stays on screen.
 */
export function TestDeviceSheet(props: TestDeviceSheetProps) {
  const { LL } = useI18nContext();
  const isMobile = useIsMobile();
  const { open, onOpenChange } = props;
  const title = LL.campaignComposer.testDevice.title();

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerContent>
          <DrawerHeader className="text-left">
            <DrawerTitle>{title}</DrawerTitle>
            <DrawerDescription>
              {LL.campaignComposer.testDevice.mobileExplanation()}
            </DrawerDescription>
          </DrawerHeader>
          <div className="flex flex-col gap-4 px-4 pb-6">
            <LinkBody {...props} variant="mobile" />
          </div>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {LL.campaignComposer.testDevice.desktopExplanation()}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          <LinkBody {...props} variant="desktop" />
        </div>
      </DialogContent>
    </Dialog>
  );
}

function LinkBody({
  phase,
  deepLink,
  ticketError,
  onRetry,
  onSend,
  isSending,
  appStoreUrl,
  playStoreUrl,
  onOpenApp,
  variant,
}: TestDeviceSheetProps & { variant: 'mobile' | 'desktop' }) {
  const { LL } = useI18nContext();

  if (phase === 'linked') {
    return (
      <>
        <p
          role="status"
          className="flex items-center gap-2 text-sm font-medium text-emerald-700 dark:text-emerald-400"
        >
          <CheckCircle2 className="size-4" aria-hidden />
          {LL.campaignComposer.testDevice.linked()}
        </p>
        <Button
          type="button"
          className={mobileLayer.touch}
          disabled={isSending}
          onClick={onSend}
        >
          {isSending
            ? LL.campaignComposer.testDevice.sending()
            : LL.campaignComposer.testDevice.send()}
        </Button>
      </>
    );
  }

  const retry = (
    <Button
      type="button"
      variant="outline"
      className={mobileLayer.touch}
      onClick={onRetry}
    >
      {LL.campaignComposer.testDevice.retry()}
    </Button>
  );

  const installHint = (appStoreUrl || playStoreUrl) && (
    <p className="text-sm text-muted-foreground">
      {LL.campaignComposer.testDevice.installHint()}{' '}
      {appStoreUrl && (
        <a
          href={appStoreUrl}
          target="_blank"
          rel="noreferrer"
          className="font-medium text-primary underline-offset-4 hover:underline"
        >
          {LL.campaignComposer.testDevice.appStore()}
        </a>
      )}
      {appStoreUrl && playStoreUrl && ' · '}
      {playStoreUrl && (
        <a
          href={playStoreUrl}
          target="_blank"
          rel="noreferrer"
          className="font-medium text-primary underline-offset-4 hover:underline"
        >
          {LL.campaignComposer.testDevice.googlePlay()}
        </a>
      )}
    </p>
  );

  if (ticketError) {
    return (
      <>
        <p role="status" className="text-sm text-destructive">
          {LL.campaignComposer.testDevice.ticketError()}
        </p>
        {retry}
      </>
    );
  }

  // Same rule as `shouldShowDeepLink`: the deep link only while waiting.
  if (phase !== 'waiting') {
    return (
      <>
        <p role="status" className="text-sm">
          {LL.campaignComposer.testDevice.timedOut()}
        </p>
        {retry}
        {installHint}
      </>
    );
  }

  const deepLinkControl =
    variant === 'mobile' ? (
      deepLink ? (
        <Button asChild className={cn('w-full', mobileLayer.touch)}>
          <a href={deepLink} onClick={onOpenApp}>
            {LL.campaignComposer.testDevice.openApp()}
          </a>
        </Button>
      ) : (
        <Button
          type="button"
          className={cn('w-full', mobileLayer.touch)}
          disabled
        >
          {LL.campaignComposer.testDevice.generatingLink()}
        </Button>
      )
    ) : (
      <div className="flex justify-center">
        <div className="rounded-lg border p-3">
          {deepLink ? (
            <QRCodeSVG
              value={deepLink}
              size={184}
              level="M"
              marginSize={1}
              title={LL.campaignComposer.testDevice.qrLabel()}
            />
          ) : (
            <Skeleton
              className="size-[184px]"
              aria-label={LL.campaignComposer.testDevice.generatingLink()}
            />
          )}
        </div>
      </div>
    );

  return (
    <>
      {deepLinkControl}
      <p
        role="status"
        className="flex items-center gap-2 text-sm text-muted-foreground"
      >
        <Loader2 className="size-4 animate-spin" aria-hidden />
        {LL.campaignComposer.testDevice.waiting()}
      </p>
      {installHint}
    </>
  );
}
